import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CaretDown,
  FloppyDisk,
  PencilSimple,
  Plus,
  Trash,
  UsersThree,
  X
} from '@phosphor-icons/react';

const OrgChartEditor = ({ role, onSave, onCancel, onDelete, isNew = false }) => {
  const [formData, setFormData] = useState({
    title: role?.title || '',
    employee_name: role?.name || '',
    department: role?.department || '',
    description: role?.description || '',
    supervisor_role_id: role?.supervisor_role_id || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete the role "${role.title}"?`)) {
      await onDelete(role.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    >
      <div className="glass rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            {isNew ? 'Add New Role' : 'Edit Role'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <X size={20} className="text-[#A0AEC0]" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="mb-6">
            <label className="block text-[#A0AEC0] text-sm font-semibold mb-2">
              Job Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl glass text-white outline-none focus:ring-2 focus:ring-[#6366F1]"
              placeholder="e.g., Head of Sales"
            />
          </div>

          {/* Employee Name */}
          <div className="mb-6">
            <label className="block text-[#A0AEC0] text-sm font-semibold mb-2">
              Employee Name
            </label>
            <input
              type="text"
              value={formData.employee_name}
              onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl glass text-white outline-none focus:ring-2 focus:ring-[#6366F1]"
              placeholder="e.g., John Smith"
            />
          </div>

          {/* Department */}
          <div className="mb-6">
            <label className="block text-[#A0AEC0] text-sm font-semibold mb-2">
              Department *
            </label>
            <input
              type="text"
              required
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              className="w-full px-4 py-3 rounded-xl glass text-white outline-none focus:ring-2 focus:ring-[#6366F1]"
              placeholder="e.g., Sales"
            />
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-[#A0AEC0] text-sm font-semibold mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 rounded-xl glass text-white outline-none focus:ring-2 focus:ring-[#6366F1] resize-none"
              placeholder="Role responsibilities and context..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 relative overflow-hidden rounded-full group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
              <span className="relative z-10 flex items-center justify-center gap-2 py-3 text-white font-bold">
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FloppyDisk size={20} />
                    {isNew ? 'Create Role' : 'Save Changes'}
                  </>
                )}
              </span>
            </button>

            {!isNew && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,0,0,0.1)] text-[#EF4444] font-semibold transition-all flex items-center gap-2"
              >
                <Trash size={18} />
                Trash
              </button>
            )}
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default OrgChartEditor;
